/*  NarrationEditor — iter100.157n
    ────────────────────────────────────────────────────────────
    Shared block used inside LessonEditor for BOTH:
      • type="narrated"   → primary content (script + generated MP3)
      • type="text"       → optional voice-over above the text body

    Actions:
      1. Write / paste a script (max 4000 chars)
      2. Pick voice + speed + model
      3. Click "Generate voice-over" → TTS via OpenAI (Emergent LLM key)
      4. …or upload a pre-recorded MP3 (≤15 MB)
      5. Preview via inline <audio>
      6. Toggle "Require full listening to progress" (gate_strict)
      7. Delete narration
*/
import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
    SpeakerHigh, MicrophoneStage, MagicWand, UploadSimple, Trash,
    Waveform, WarningCircle, CheckCircle,
} from "@phosphor-icons/react";

import { api, API } from "@/lib/api";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

const VOICE_OPTIONS = [
    { key: "nova", label: "Nova — energetic, upbeat" },
    { key: "alloy", label: "Alloy — neutral, balanced" },
    { key: "shimmer", label: "Shimmer — bright, cheerful" },
    { key: "onyx", label: "Onyx — deep, authoritative" },
    { key: "fable", label: "Fable — expressive, storytelling" },
    { key: "echo", label: "Echo — smooth, calm" },
    { key: "coral", label: "Coral — warm, friendly" },
    { key: "sage", label: "Sage — wise, measured" },
    { key: "ash", label: "Ash — clear, articulate" },
];

const SPEED_OPTIONS = [0.75, 1.0, 1.25, 1.5];
const MODEL_OPTIONS = [
    { key: "tts-1-hd", label: "HD (recommended)" },
    { key: "tts-1", label: "Standard (faster)" },
];
const MAX_SCRIPT = 4000;

function fmtBytes(n) {
    if (!n) return "";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

export default function NarrationEditor({ lesson, readOnly, onLessonRefresh, variant = "full" }) {
    const hasNarration = !!lesson?.narration_media_path;
    const [script, setScript] = useState(lesson?.narration_script || "");
    const [voice, setVoice] = useState(lesson?.narration_voice || "nova");
    const [model, setModel] = useState(lesson?.narration_model || "tts-1-hd");
    const [speed, setSpeed] = useState(lesson?.narration_speed || 1.0);
    const [gateStrict, setGateStrict] = useState(!!lesson?.narration_gate_strict);
    const [generating, setGenerating] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [audioBlobUrl, setAudioBlobUrl] = useState(null);
    const [audioLoading, setAudioLoading] = useState(false);
    const fileInputRef = useRef(null);

    useEffect(() => {
        setScript(lesson?.narration_script || "");
        setVoice(lesson?.narration_voice || "nova");
        setModel(lesson?.narration_model || "tts-1-hd");
        setSpeed(lesson?.narration_speed || 1.0);
        setGateStrict(!!lesson?.narration_gate_strict);
    }, [lesson?.id]);

    // Load the audio blob whenever there IS narration
    useEffect(() => {
        setAudioBlobUrl(null);
        if (!hasNarration || !lesson?.id) return;
        let cancelled = false;
        setAudioLoading(true);
        (async () => {
            try {
                const token = localStorage.getItem("ca_token");
                const resp = await fetch(
                    `${API}/course-builder/lessons/${lesson.id}/narration/audio`,
                    { headers: { Authorization: `Bearer ${token}` } },
                );
                if (!resp.ok) throw new Error(await resp.text());
                const blob = await resp.blob();
                if (!cancelled) setAudioBlobUrl(URL.createObjectURL(blob));
            } catch (e) {
                if (!cancelled) toast.error("Couldn't load audio preview");
            } finally {
                if (!cancelled) setAudioLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [hasNarration, lesson?.id, lesson?.narration_generated_at]);

    const chars = script.length;
    const overLimit = chars > MAX_SCRIPT;

    const onGenerate = async () => {
        if (readOnly || generating) return;
        const text = script.trim();
        if (!text) {
            toast.error("Write a script first.");
            return;
        }
        if (text.length > MAX_SCRIPT) {
            toast.error(`Script must be ≤ ${MAX_SCRIPT} characters.`);
            return;
        }
        try {
            setGenerating(true);
            toast.info("Synthesising voice-over…");
            const { data } = await api.post(
                `/course-builder/lessons/${lesson.id}/narration/generate`,
                { text, voice, model, speed },
            );
            toast.success(`Voice-over ready · ${fmtBytes(data.size_bytes)}`);
            await onLessonRefresh?.(data.lesson);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Voice-over failed.");
        } finally {
            setGenerating(false);
        }
    };

    const onUpload = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (f.size > 15 * 1024 * 1024) {
            toast.error("Audio must be ≤ 15 MB.");
            e.target.value = "";
            return;
        }
        const form = new FormData();
        form.append("file", f);
        try {
            setUploading(true);
            const { data } = await api.post(
                `/course-builder/lessons/${lesson.id}/narration/upload`,
                form,
                { headers: { "Content-Type": "multipart/form-data" } },
            );
            toast.success(`MP3 uploaded · ${fmtBytes(data.size_bytes)}`);
            await onLessonRefresh?.(data.lesson);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Upload failed.");
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    const onDelete = async () => {
        if (readOnly) return;
        if (!window.confirm("Remove voice-over from this lesson?")) return;
        try {
            await api.delete(`/course-builder/lessons/${lesson.id}/narration`);
            toast.success("Voice-over removed.");
            await onLessonRefresh?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't remove.");
        }
    };

    const onGateStrictToggle = async (next) => {
        setGateStrict(next);
        try {
            await api.put(`/course-builder/lessons/${lesson.id}`, { narration_gate_strict: next });
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't save setting.");
            setGateStrict(!next);
        }
    };

    return (
        <div
            className="rounded-xl border border-sky-200 bg-sky-50/40 p-4 space-y-4"
            data-testid={`narration-editor-${lesson.id}`}
        >
            <div className="flex items-center gap-2">
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-sky-100 text-sky-700">
                    <SpeakerHigh size={16} weight="fill" />
                </span>
                <div className="flex-1">
                    <div className="text-sm font-semibold flex items-center gap-2">
                        Voice-over narration
                        {hasNarration && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 text-[10px] font-bold">
                                <CheckCircle size={9} weight="fill" /> Ready
                            </span>
                        )}
                    </div>
                    <div className="text-[11px] text-muted-foreground">
                        {variant === "full"
                            ? "Narrated Slide lessons pair a slide body with an audio track. Doctors can only progress after listening to ≥ 90%."
                            : "Optional. Adds an audio player above the text lesson body for doctors who prefer listening."}
                    </div>
                </div>
            </div>

            {/* Existing audio preview */}
            {hasNarration && (
                <div className="rounded-lg bg-white border border-border p-3 space-y-2">
                    <div className="flex items-center gap-2 flex-wrap text-[11px] text-muted-foreground">
                        <span className="inline-flex items-center gap-1 text-foreground font-semibold">
                            <Waveform size={12} weight="fill" className="text-primary" />
                            {lesson.narration_source === "tts" ? "AI voice-over" : "Uploaded MP3"}
                        </span>
                        {lesson.narration_voice && <span>· voice: <b className="text-foreground">{lesson.narration_voice}</b></span>}
                        {lesson.narration_model && <span>· model: {lesson.narration_model}</span>}
                        {lesson.narration_speed && <span>· {lesson.narration_speed}×</span>}
                        {lesson.narration_size_bytes && <span>· {fmtBytes(lesson.narration_size_bytes)}</span>}
                    </div>
                    {audioLoading ? (
                        <div className="text-xs text-muted-foreground italic">Loading preview…</div>
                    ) : audioBlobUrl ? (
                        <audio
                            controls
                            src={audioBlobUrl}
                            className="w-full"
                            data-testid={`narration-preview-${lesson.id}`}
                        />
                    ) : (
                        <div className="text-xs text-muted-foreground italic">Preview unavailable.</div>
                    )}
                    {!readOnly && (
                        <div className="flex items-center justify-end">
                            <button
                                type="button"
                                onClick={onDelete}
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs text-rose-600 hover:bg-rose-50 border border-rose-200"
                                data-testid={`narration-delete-${lesson.id}`}
                            >
                                <Trash size={11} weight="bold" /> Remove voice-over
                            </button>
                        </div>
                    )}
                </div>
            )}

            {/* Script / voice choices */}
            <div className="space-y-3">
                <label className="block">
                    <span className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground inline-flex items-center gap-1">
                        <MicrophoneStage size={11} weight="duotone" /> Script
                        <span className="ml-auto text-[10px] font-mono text-muted-foreground normal-case tracking-normal">
                            {chars} / {MAX_SCRIPT}
                        </span>
                    </span>
                    <Textarea
                        rows={variant === "full" ? 6 : 4}
                        disabled={readOnly}
                        value={script}
                        onChange={(e) => setScript(e.target.value)}
                        placeholder="Write the narration text. Keep sentences short and natural — this is what the voice will read aloud."
                        className="mt-1 font-mono text-sm"
                        data-testid={`narration-script-${lesson.id}`}
                        maxLength={MAX_SCRIPT + 200}
                    />
                    {overLimit && (
                        <div className="text-[11px] text-rose-600 mt-1 flex items-center gap-1">
                            <WarningCircle size={11} weight="fill" /> Script is over the {MAX_SCRIPT}-character limit.
                        </div>
                    )}
                </label>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <label className="block">
                        <span className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Voice</span>
                        <select
                            disabled={readOnly}
                            value={voice}
                            onChange={(e) => setVoice(e.target.value)}
                            className="mt-1 w-full h-9 px-2 rounded-md border border-border bg-white text-sm"
                            data-testid={`narration-voice-${lesson.id}`}
                        >
                            {VOICE_OPTIONS.map((v) => (
                                <option key={v.key} value={v.key}>{v.label}</option>
                            ))}
                        </select>
                    </label>
                    <label className="block">
                        <span className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Model</span>
                        <select
                            disabled={readOnly}
                            value={model}
                            onChange={(e) => setModel(e.target.value)}
                            className="mt-1 w-full h-9 px-2 rounded-md border border-border bg-white text-sm"
                            data-testid={`narration-model-${lesson.id}`}
                        >
                            {MODEL_OPTIONS.map((m) => (
                                <option key={m.key} value={m.key}>{m.label}</option>
                            ))}
                        </select>
                    </label>
                    <label className="block">
                        <span className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Speed</span>
                        <select
                            disabled={readOnly}
                            value={String(speed)}
                            onChange={(e) => setSpeed(Number(e.target.value))}
                            className="mt-1 w-full h-9 px-2 rounded-md border border-border bg-white text-sm"
                            data-testid={`narration-speed-${lesson.id}`}
                        >
                            {SPEED_OPTIONS.map((s) => (
                                <option key={s} value={s}>{s}×</option>
                            ))}
                        </select>
                    </label>
                </div>
            </div>

            {/* Actions row */}
            {!readOnly && (
                <div className="flex items-center gap-2 flex-wrap pt-1">
                    <Button
                        onClick={onGenerate}
                        disabled={generating || uploading || overLimit || !script.trim()}
                        data-testid={`narration-generate-${lesson.id}`}
                    >
                        <MagicWand size={13} weight="fill" className="mr-1.5" />
                        {generating ? "Generating…" : hasNarration ? "Regenerate voice-over" : "Generate voice-over"}
                    </Button>

                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="audio/mpeg,audio/mp3,audio/*"
                        hidden
                        onChange={onUpload}
                        data-testid={`narration-upload-input-${lesson.id}`}
                    />
                    <Button
                        variant="outline"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading || generating}
                        data-testid={`narration-upload-btn-${lesson.id}`}
                    >
                        <UploadSimple size={13} weight="fill" className="mr-1.5" />
                        {uploading ? "Uploading…" : "Upload MP3 instead"}
                    </Button>

                    <label className="ml-auto inline-flex items-center gap-2 px-2 py-1 rounded-md border border-border bg-white text-xs cursor-pointer">
                        <input
                            type="checkbox"
                            checked={gateStrict}
                            onChange={(e) => onGateStrictToggle(e.target.checked)}
                            data-testid={`narration-gate-strict-${lesson.id}`}
                        />
                        <span className="font-semibold">Require full listening to progress</span>
                    </label>
                </div>
            )}
        </div>
    );
}
